import { Element } from '@craftjs/core';
import { ReactComponent as ContainerIcon } from 'assets/img/generalBuilder/container_24.svg';
import { ReactComponent as RepeatableContainerIcon } from 'assets/img/generalBuilder/repeatable_block_24.svg';
import { ReactComponent as DividingLineIcon } from 'assets/img/generalBuilder/line_24.svg';
import { ReactComponent as TextIcon } from 'assets/img/generalBuilder/text_24.svg';
import { ReactComponent as ImageInputIcon } from 'assets/img/generalBuilder/pic_24.svg';
import { ReactComponent as TextInputIcon } from 'assets/img/generalBuilder/single_line_24.svg';
import { ReactComponent as MultipleTextInputIcon } from 'assets/img/generalBuilder/multiple_line_24.svg';
import { ReactComponent as NumberInputIcon } from 'assets/img/generalBuilder/number_24.svg';
import { ReactComponent as SliderInputIcon } from 'assets/img/generalBuilder/slider_24.svg';
import { ReactComponent as DateInputIcon } from 'assets/img/generalBuilder/calendar_24.svg';
import { ReactComponent as DateRangeInputIcon } from 'assets/img/generalBuilder/calendar_range_24.svg';
import { ReactComponent as TimeInputIcon } from 'assets/img/generalBuilder/clock_24.svg';
import { ReactComponent as CheckBoxInputIcon } from 'assets/img/generalBuilder/checkbox_24.svg';
import { ReactComponent as RadioInputIcon } from 'assets/img/generalBuilder/radio_24.svg';
import { ReactComponent as DropdownInputIcon } from 'assets/img/generalBuilder/dropdown_24.svg';
import { ReactComponent as FileInputIcon } from 'assets/img/generalBuilder/file_24.svg';
import { ReactComponent as TagInputIcon } from 'assets/img/generalBuilder/tag_box_24.svg';
import { IStudyNoteControlsResolver } from './IStudyNoteControlsResolver';
import { SelectorGroupEnum, SelectorResolverType } from '../builder/generalFormBuilder.t';
import { Container, RepeatableContainer, RootContainer, RepeatableSection } from './builderComponents/containers';
import { TextBlock, FormDividingLine, ImageBlock } from './builderComponents/content';
import { useScopedTranslation } from 'src/application/localisation/useScopedTranslation';
import {
  FormCheckboxGroupInput,
  FormDateInput,
  FormDateRangeInput,
  FormDropDownInput,
  FormMultipleDropdownInput,
  FormMultipleTextInput,
  FormNumberInput,
  FormRadioGroupInput,
  FormSliderInput,
  FormTagInput,
  FormTextInput,
  FormTimeInput,
} from './builderComponents/inputs';
import { FormFileInput } from './builderComponents/inputs/formFileInput';
import {
  formRepeatableContainerDefaultProps,
  formRepeatableSectionDefaultProps,
} from '../builder/types/RepeatableContainer';
import { formTextDefaultProps } from '../builder/types/Text';
import { formMultipleTextInputDefaultProps, formTextInputDefaultProps } from '../builder/types/TextInput';
import { formNumberInputDefaultProps } from '../builder/types/NumberInput';
import { sliderInputDefaultProps } from '../builder/types/SliderInput';
import {
  formDateInputDefaultProps,
  formDateRangeInputDefaultProps,
  formTimeInputDefaultProps,
} from '../builder/types/DateInput';
import { formRadioGroupInputDefaultProps } from '../builder/types/RadioGroupInput';
import { formDropdownInputDefaultProps, formMultipleDropdownInputDefaultProps } from '../builder/types/DropdownInput';
import { formCheckboxGroupInputDefaultProps } from '../builder/types/CheckboxGroupInput';
import { formTagInputDefaultProps } from '../builder/types/TagInput';
import { formFileInputDefaultProps } from '../builder/types/FileInput';

export type StudyNoteSelectorResolverType = SelectorResolverType<IStudyNoteControlsResolver>;

export const useStudyNoteSelectors = () => {
  const { t } = useScopedTranslation('GeneralFormBuilder.Panels.Controls');

  return {
    // Containers
    Container: {
      Icon: ContainerIcon,
      name: t('FormContainer'),
      group: SelectorGroupEnum.Containers,
      userComponent: <Element canvas={true} is={Container} />,
    },
    RepeatableContainer: {
      Icon: RepeatableContainerIcon,
      name: t('RepeatableContainer'),
      group: SelectorGroupEnum.Containers,
      userComponent: <Element canvas={true} is={RepeatableContainer} {...formRepeatableContainerDefaultProps} />,
    },
    RepeatableSection: {
      Icon: RepeatableContainerIcon,
      name: '',
      group: SelectorGroupEnum.None,
      userComponent: <Element canvas={true} is={RepeatableSection} {...formRepeatableSectionDefaultProps} />,
    },
    RootContainer: {
      Icon: ContainerIcon,
      name: t('RootContainer'),
      group: SelectorGroupEnum.None,
      userComponent: <Element canvas={true} is={RootContainer} />,
    },
    // Content
    FormDividingLine: {
      Icon: DividingLineIcon,
      name: t('FormDividingLine'),
      group: SelectorGroupEnum.Containers,
      userComponent: <Element is={FormDividingLine} />,
    },
    FormText: {
      Icon: TextIcon,
      name: t('FormText'),
      group: SelectorGroupEnum.Content,
      userComponent: <Element is={TextBlock} {...formTextDefaultProps} />,
    },
    FormImage: {
      Icon: ImageInputIcon,
      name: t('FormImage'),
      group: SelectorGroupEnum.Content,
      userComponent: <Element is={ImageBlock} />,
    },
    // Fields
    FormTextInput: {
      Icon: TextInputIcon,
      name: t('FormTextInput'),
      group: SelectorGroupEnum.InputFields,
      userComponent: <Element is={FormTextInput} {...formTextInputDefaultProps} />,
    },
    FormMultipleTextInput: {
      Icon: MultipleTextInputIcon,
      name: t('FormMultipleTextInput'),
      group: SelectorGroupEnum.InputFields,
      userComponent: <Element is={FormMultipleTextInput} {...formMultipleTextInputDefaultProps} />,
    },
    FormNumberInput: {
      Icon: NumberInputIcon,
      name: t('FormNumberInput'),
      group: SelectorGroupEnum.InputFields,
      userComponent: <Element is={FormNumberInput} {...formNumberInputDefaultProps} />,
    },
    FormSliderInput: {
      Icon: SliderInputIcon,
      name: t('FormSliderInput'),
      group: SelectorGroupEnum.InputFields,
      userComponent: <Element is={FormSliderInput} {...sliderInputDefaultProps} />,
    },
    FormDateInput: {
      Icon: DateInputIcon,
      name: t('FormDateInput'),
      group: SelectorGroupEnum.Time,
      userComponent: <Element is={FormDateInput} {...formDateInputDefaultProps} />,
    },
    FormDateRangeInput: {
      Icon: DateRangeInputIcon,
      name: t('FormDateRangeInput'),
      group: SelectorGroupEnum.Time,
      userComponent: <Element is={FormDateRangeInput} {...formDateRangeInputDefaultProps} />,
    },
    FormTimeInput: {
      Icon: TimeInputIcon,
      name: t('FormTimeInput'),
      group: SelectorGroupEnum.Time,
      userComponent: <Element is={FormTimeInput} {...formTimeInputDefaultProps} />,
    },
    FormRadioGroupInput: {
      Icon: RadioInputIcon,
      name: t('FormRadioGroupInput'),
      group: SelectorGroupEnum.SingleChoice,
      userComponent: <Element is={FormRadioGroupInput} {...formRadioGroupInputDefaultProps} />,
    },
    FormDropdownInput: {
      Icon: DropdownInputIcon,
      name: t('FormDropdownInput'),
      group: SelectorGroupEnum.SingleChoice,
      userComponent: <Element is={FormDropDownInput} {...formDropdownInputDefaultProps} />,
    },
    FormCheckboxGroupInput: {
      Icon: CheckBoxInputIcon,
      name: t('FormCheckboxGroupInput'),
      group: SelectorGroupEnum.MultipleChoice,
      userComponent: <Element is={FormCheckboxGroupInput} {...formCheckboxGroupInputDefaultProps} />,
    },
    FormMultipleDropdownInput: {
      Icon: DropdownInputIcon,
      name: t('FormMultipleDropdownInput'),
      group: SelectorGroupEnum.MultipleChoice,
      userComponent: <Element is={FormMultipleDropdownInput} {...formMultipleDropdownInputDefaultProps} />,
    },
    FormTagInput: {
      Icon: TagInputIcon,
      name: t('FormTagInput'),
      group: SelectorGroupEnum.MultipleChoice,
      userComponent: <Element is={FormTagInput} {...formTagInputDefaultProps} />,
    },
    FormFileInput: {
      Icon: FileInputIcon,
      name: t('FormFileInput'),
      group: SelectorGroupEnum.OtherInput,
      userComponent: <Element is={FormFileInput} {...formFileInputDefaultProps} />,
    },
  } satisfies StudyNoteSelectorResolverType;
};
